import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Hammer, LayoutDashboard, FilePlus2, ClipboardList, Users, Building2, BookmarkPlus, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PlanChip } from "@/components/PlanChip";

const navItems = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
  { to: "/app/new", icon: FilePlus2, label: "New Quote", testid: "nav-new-quote" },
  { to: "/app/quotes", icon: ClipboardList, label: "Saved Quotes", testid: "nav-saved-quotes" },
  { to: "/app/clients", icon: Users, label: "Clients", testid: "nav-clients" },
  { to: "/app/templates", icon: BookmarkPlus, label: "Templates", testid: "nav-templates" },
  { to: "/app/profile", icon: Building2, label: "Company Profile", testid: "nav-profile" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-slate-200 bg-white flex-col">
        <div className="h-16 flex items-center px-5 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2" data-testid="sidebar-logo-link">
            <div className="h-8 w-8 rounded-lg bg-orange-500 flex items-center justify-center text-white">
              <Hammer className="h-4 w-4" />
            </div>
            <span className="font-display text-base font-bold text-slate-900">ScopeWriter <span className="text-orange-500">AI</span></span>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-orange-50 text-orange-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="px-3 py-2">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <PlanChip />
          <Button
            variant="ghost" size="sm"
            onClick={onLogout} data-testid="logout-button"
            className="w-full justify-start text-slate-600 hover:text-slate-900 mt-1"
          >
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-orange-500 flex items-center justify-center text-white">
            <Hammer className="h-3.5 w-3.5" />
          </div>
          <span className="font-display font-bold text-slate-900">ScopeWriter</span>
        </Link>
        <Button variant="ghost" size="sm" onClick={onLogout} data-testid="mobile-logout-button">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      <main className="flex-1 md:ml-0 pt-14 md:pt-0 min-w-0">
        <div className="p-5 sm:p-8">
          <Outlet />
        </div>
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-6 z-30">
          {navItems.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[10px] ${
                  isActive ? "text-orange-600" : "text-slate-500"
                }`
              }
            >
              <n.icon className="h-4 w-4 mb-0.5" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
        <div className="md:hidden h-16" />
      </main>
    </div>
  );
}
